import { Injectable, inject } from '@angular/core';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Platform } from '@ionic/angular/standalone';
import { EMPTY, of, switchMap } from 'rxjs';
import { IAppUpdateCheckDto, TranslationKeys } from 'src/app/contracts';
import { Api } from 'src/app/core/common/api';
import { ILiveUpdateParsedVersion, IVersionResult, VersionStatus } from 'src/app/core/contracts';
import { Logger } from 'src/app/core/helpers/logger';
import { UI } from 'src/app/core/helpers/ui';
import { isWeb, toHttpParams } from 'src/app/core/helpers/utils';
import { environment } from 'src/environments/environment';

@Injectable()
export class UpdateHandler {
  private platform = inject(Platform);
  private logger = inject(Logger);
  private api = inject(Api);
  private ui = inject(UI);
  private browser = inject(InAppBrowser);

  public checkForUpdates(updateCheck?: IAppUpdateCheckDto) {
    if (isWeb) {
      // if it's a web app then it's already up to date!
      return;
    }

    const checkStore = updateCheck ? updateCheck.checkForStoreUpdate : true; // default to true for backward compatibility, in case updateCheck is not provided
    const checkLive = updateCheck ? updateCheck.checkForLiveUpdate : false; // only check for live updates if explicitly enabled

    const store$ = checkStore ? this.checkForStoreUpdate() : of(null);
    store$.pipe(switchMap(() => (checkLive ? this.checkForLiveUpdate() : EMPTY))).subscribe();
  }
  private checkForStoreUpdate() {
    return this.api
      .get<IVersionResult>('version/status', {
        params: toHttpParams({
          platform: this.platform.is('ios') ? 'ios' : 'android',
          versionCode: environment.version,
        }),
        handleError: false,
        handleLoading: false,
      })
      .pipe(
        switchMap(result => {
          switch (result?.status) {
            case VersionStatus.UpdateRequired:
            case VersionStatus.UpdateRecommended:
              if (result.updateUrl) {
                return this.showDialog(result);
              }
              break;
            default:
              // no update, app is up to date
              break;
          }
          return of(null);
        })
      );
  }
  private async checkForLiveUpdate() {
    try {
      if (environment.capgoLiveUpdateChannel && environment.capgoLiveUpdateChannel !== 'production') {
        const channel = await CapacitorUpdater.getChannel();
        if (channel?.channel !== environment.capgoLiveUpdateChannel) {
          await CapacitorUpdater.setChannel({ channel: environment.capgoLiveUpdateChannel }).catch(err =>
            this.logger.error(`@update.handler: Failed to set Capgo channel to '${environment.capgoLiveUpdateChannel}'`, err)
          );
        }
      }

      const latest = await CapacitorUpdater.getLatest();
      const current = await CapacitorUpdater.current();
      const currentVersion = current?.bundle?.version;

      if (!latest?.version) {
        return;
      }

      if (!this.isNewerVersion(latest.version, currentVersion)) {
        return;
      }

      this.logger.log(`@update.handler: Live update available: ${currentVersion ?? 'none'} → ${latest.version}`);

      const bundle = await CapacitorUpdater.download({ url: latest.url, version: latest.version });
      const alert = await this.ui.showMessage(TranslationKeys.Common.ANewUpdateHasBeenDownloaded, null, null, TranslationKeys.Common.Install, false);
      await alert.onDidDismiss();
      await CapacitorUpdater.set({ id: bundle.id });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      if ((err as any)?.message === 'no_new_version_available') {
        // no new version available
        return;
      }
      this.logger.error('@update.handler: Unexpected error while checking for live update:', err);
    }
  }
  private async showDialog(result: IVersionResult): Promise<void> {
    const canDismiss = result.status === VersionStatus.UpdateRecommended;
    const update = await this.ui.showUpdateDialog(canDismiss);
    if (update && result.updateUrl) {
      this.browser.create(result.updateUrl, '_system');
    }
  }

  /**
   * Parses version strings:
   * - "1.0.0"              → base only
   * - "1.0.0-staging.12"   → staging channel, build 12
   * - "1.0.0-production.3" → production channel, build 3
   */
  private parseVersion(version: string): ILiveUpdateParsedVersion | null {
    const match = version.match(/^(\d+)\.(\d+)\.(\d+)(?:-([a-z]+)\.(\d+))?$/);
    if (!match) return null;
    return {
      major: Number(match[1]),
      minor: Number(match[2]),
      patch: Number(match[3]),
      channel: match[4] || null,
      build: match[5] ? Number(match[5]) : -1,
    };
  }

  /**
   * Returns true if `latest` is a newer version than `current`.
   * Handles formats: "1.0.0", "1.0.0-staging.12"
   * Returns true when current is null/undefined/builtin (first install).
   */
  private isNewerVersion(latest: string, current: string | null | undefined): boolean {
    if (!current || current === 'builtin') return true;
    if (latest === current) return false;

    const l = this.parseVersion(latest);
    const c = this.parseVersion(current);
    if (!l || !c) return latest !== current;

    if (l.major !== c.major) return l.major > c.major;
    if (l.minor !== c.minor) return l.minor > c.minor;
    if (l.patch !== c.patch) return l.patch > c.patch;

    // Same base version — compare channel build numbers
    if (l.channel && c.channel) return l.build > c.build;

    // Latest has channel suffix but current doesn't → treat as update (e.g. fresh native install receiving staging bundle)
    if (l.channel && !c.channel) return true;

    return false;
  }
}
